import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

/**
 * useTheme hook — Golden Hour Design System
 *
 * Provides access to current theme and toggle function.
 * Syncs with document.documentElement.classList ('dark' class).
 * Persists theme preference to localStorage.
 *
 * @example
 * ```tsx
 * const { theme, setTheme, toggleTheme, isDark } = useTheme();
 *
 * // Check current theme
 * if (isDark) { ... }
 *
 * // Toggle between light and dark
 * <button onClick={toggleTheme}>Toggle Theme</button>
 *
 * // Set specific theme
 * <button onClick={() => setTheme('dark')}>Dark Mode</button>
 * ```
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Initialize from localStorage or system preference
    if (typeof window === 'undefined') return 'light';

    const stored = localStorage.getItem('y-theme') as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;

    // Fallback to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    localStorage.setItem('y-theme', theme);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prev => prev === 'light' ? 'dark' : 'light');
  };

  return {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };
}
